import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { ReportType, SharedDriveModel } from '../../../../../types';
import { ReportFormValues } from '../reports-form-page';
import { useQuery } from '@tanstack/react-query';
import { getAllResourceCalendars, UserKeys } from '../../../../api/users-api';
import SharedReviewLayout, {
  ReviewSection,
} from '../../../../shared/shared-review-layout';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

interface StepReviewProps {
  selectedReport: ReportFormValues;
  selectedReportType?: ReportType | null;
  dialogType: string;
  selectedSharedDrives?: SharedDriveModel[];
}

export default function StepReview({
  selectedReport,
  selectedReportType,
  dialogType,
  selectedSharedDrives,
}: StepReviewProps) {
  const { data: resourceCalendars } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
    enabled: !!(selectedReport.sourceType === 'Resource_Calendar'),
  });

  const calendarEmailToNameMap = useMemo(() => {
    if (!resourceCalendars) {
      return new Map<string, string>();
    }
    return new Map(
      resourceCalendars.map((c) => [c.resourceEmail, c.resourceName])
    );
  }, [resourceCalendars]);

  const formatSourceType = (type: string | null): string => {
    switch (type) {
      case 'Resource_Calendar':
        return 'Resource Calendar';
      case 'Shared_Drive':
        return 'Shared Drive';
      case null:
        return 'N/A';
      default:
        return type;
    }
  };

  const manualRun = selectedReport.manualRun;
  const scheduled = selectedReport.scheduled;
  const times = selectedReport.times || [];
  const dates = selectedReport.dates || [];

  const [showAll, setShowAll] = useState(false);
  const columns = selectedReport.columns || [];
  const destinationFolder = selectedReport.destFolderName || '';
  const MAX_CHIPS = 8;
  const hiddenCount = columns.length - MAX_CHIPS;

  const notifyViaEmail = selectedReport._notifyViaEmail;
  const emailsToNotify = selectedReport.emailsToNotify || [];
  const allSource = selectedReport.allSource;
  const sourceType = selectedReport.sourceType || '';
  const sources = selectedReport.sources || [];
  const permissions = selectedReport.permissions || [];
  const hasEmailShareTo =
    (notifyViaEmail && emailsToNotify.length > 0) ||
    (permissions && permissions.length > 0);

  const reportSections: ReviewSection[] = [
    {
      title: 'Basic Information',
      leftWidth: {
        labelWidth: hasEmailShareTo ? 6 : 3,
        valueWidth: hasEmailShareTo ? 6 : 9,
      },
      leftItems: [
        { label: 'Report Name', value: selectedReport.reportName },
        {
          label: 'Generated File Name',
          value: selectedReport.generatedFileName,
        },
        { label: 'Report Type', value: selectedReportType?.niceName },
        {
          label: 'Description',
          value: selectedReport.description,
          isVisible: selectedReport.description?.length > 0,
        },
        { label: 'Manual Run', value: manualRun, isVisible: manualRun },
        { label: 'Scheduled Run', value: scheduled, isVisible: scheduled },
        {
          label: 'Destination Folder',
          value: destinationFolder,
          isVisible: destinationFolder.length > 0,
        },
        {
          label: 'Created Date',
          value: selectedReport.createdDate ? selectedReport.createdDate : '',
          variant: 'date',
          isVisible: !!(dialogType === 'details' && selectedReport.createdDate),
        },
        {
          label: 'Created By',
          value: [{ email: selectedReport.createdBy, type: 'User' }],
          variant: 'chip',
          isVisible: !!(dialogType === 'details' && selectedReport.createdBy),
        },
        {
          label: 'Last Run Date',
          value: selectedReport.lastRunDate
            ? new Date(selectedReport.lastRunDate).toLocaleString()
            : 'Never Ran',
          isVisible: !!(dialogType === 'details'),
        },
      ],
      rightItems: [
        {
          label: 'Email Report To',
          value: emailsToNotify.map((email) => ({
            email: email,
            type: 'User' as const,
          })),
          variant: 'chip',
          flex: true,
          isVisible: !!(notifyViaEmail && emailsToNotify.length > 0),
        },
        {
          label: 'Share Access To',
          value: permissions.map((p) => ({
            email: p.userEmail,
            type: 'User' as const,
          })),
          variant: 'chip',
          flex: true,
          isVisible: !!(permissions.length > 0),
        },
      ],
    },

    {
      title: 'Configuration',
      leftWidth: {
        labelWidth: 3,
        valueWidth: 9,
      },
      leftItems: [
        {
          label: 'Date Range',
          value: selectedReport?.dateRange
            ? `${dayjs(selectedReport.dateRange[0]).format(
                'MM/DD/YYYY'
              )} - ${dayjs(selectedReport.dateRange[1]).format('MM/DD/YYYY')}`
            : selectedReport.startDate && selectedReport.endDate
            ? `${dayjs(selectedReport.startDate).format(
                'MM/DD/YYYY'
              )} - ${dayjs(selectedReport.endDate).format('MM/DD/YYYY')}`
            : '-',
          isVisible: !!(
            selectedReportType?.hasDateRange &&
            !(
              selectedReport.reportOnLastXDays! > 0 ||
              selectedReport.xDaysSelected
            )
          ),
        },
        {
          label: 'Date Selection',
          value: selectedReport.startDate
            ? dayjs(selectedReport.startDate).format('MM/DD/YYYY')
            : dayjs().subtract(3, 'day').format('MM/DD/YYYY'),
          isVisible: selectedReportType?.hasDateSelection,
        },
        {
          label: 'External Email Address',
          value: selectedReport.externalEmailAddress,
          isVisible: !!(
            selectedReport.externalEmailAddress &&
            selectedReport.reportType === 'externallyOwnedDocs'
          ),
        },
        {
          label: 'RegEx Type',
          value: selectedReport.regexTypeSelectedList?.map((c) => ({
            email: c,
            type: 'Unknown' as const,
          })),
          variant: 'chip',
          flex: true,
          isVisible: !!(
            selectedReport.regexTypeSelectedList?.length &&
            selectedReportType?.hasAdditionalConfiguration
          ),
        },
        {
          label: 'RegEx',
          value: selectedReport.regex,
          isVisible: !!(
            selectedReport.regex &&
            selectedReportType?.hasAdditionalConfiguration
          ),
        },
        {
          label: 'Days Back',
          value: selectedReport.daysback
            ? `${selectedReport.daysback} ${
                selectedReport.daysback > 1 ? 'Days' : 'Day'
              }`
            : '',
          isVisible: !!(
            selectedReport.daysback != null &&
            selectedReport.reportType === 'docsModOverXdays'
          ),
        },
        {
          label: 'Days Back',
          value: `Past ${selectedReport.reportOnLastXDays} ${
            selectedReport.reportOnLastXDays! > 1 ? 'Days' : 'Day'
          }`,
          isVisible: !!(
            selectedReport.xDaysSelected ||
            selectedReport.reportOnLastXDays! > 0
          ),
        },
        {
          label: 'Inactive Users Criteria',
          value:
            selectedReport.neverLoggedInCheck &&
            selectedReport.inactiveSinceDate
              ? `Include users who have never logged in and inactive since ${dayjs(
                  selectedReport.inactiveSinceDate
                ).format('MM/DD/YYYY')}`
              : selectedReport.neverLoggedInCheck
              ? 'Include users who have never logged in'
              : `Include users who are inactive since ${dayjs(
                  selectedReport.inactiveSinceDate
                ).format('MM/DD/YYYY')}`,
          isVisible: !!(
            selectedReport.reportType === 'inactiveUsers' &&
            (selectedReport.neverLoggedInCheck ||
              selectedReport.inactiveSinceDate)
          ),
        },
      ],
    },

    {
      title: 'Columns',
      leftItems: [
        {
          value: columns
            ?.slice(0, showAll ? columns.length : MAX_CHIPS)
            .map((c) => ({ email: c.columnName, type: 'Unknown' as const })),
          variant: 'chip',
          isVisible: selectedReportType?.hasColumns && columns?.length > 0,
        },
        {
          value: columns && columns.length > MAX_CHIPS && (
            <Box
              sx={{ display: 'flex', width: '100%', justifyContent: 'center' }}
            >
              <Button size="small" onClick={() => setShowAll(!showAll)}>
                {showAll ? 'Show less' : `+${hiddenCount} more`}
              </Button>
            </Box>
          ),
          isVisible:
            selectedReportType?.hasColumns && columns?.length > MAX_CHIPS,
        },
      ],
    },

    {
      title: 'Sources',
      leftItems: [
        {
          label: 'Source Type',
          value: formatSourceType(selectedReport.sourceType),
          isVisible: !!(
            selectedReport.sourceType &&
            [
              'User',
              'Group',
              'OrgUnit',
              'Domain',
              'Resource_Calendar',
              'Shared_Drive',
            ].includes(selectedReport.sourceType)
          ),
        },
        {
          label: 'Sources',
          value: allSource
            ? 'All'
            : sources?.map((s) => {
                const type =
                  sourceType === 'User'
                    ? ('User' as const)
                    : sourceType === 'Group'
                    ? ('Group' as const)
                    : sourceType === 'gPanel_Role'
                    ? ('gPanel Role' as const)
                    : sourceType === 'Resource_Calendar'
                    ? ('Resource Calendar' as const)
                    : sourceType === 'OrgUnit'
                    ? ('OrgUnit' as const)
                    : sourceType === 'Shared_Drive'
                    ? ('Shared Drive' as const)
                    : ('Domain' as const);
                const label =
                  sourceType === 'Resource_Calendar'
                    ? calendarEmailToNameMap.get(s) || s
                    : sourceType === 'Shared_Drive' && selectedSharedDrives
                    ? selectedSharedDrives.find((drive) => drive.id === s)
                        ?.name ?? s
                    : s;
                return {
                  id: s,
                  email: label,
                  type: type,
                };
              }),
          variant: allSource ? 'default' : 'chip',
          flex: allSource ? false : true,
          isVisible: !!((sources && sources.length > 0) || allSource),
        },
      ],
    },
    ...(scheduled
      ? [
          {
            title: 'Schedule',
            leftItems: [
              {
                label: 'Times',
                value: times.map((time) =>
                  parseInt(time) === 0
                    ? '12 AM'
                    : parseInt(time) < 12
                    ? `${time} AM`
                    : parseInt(time) === 12
                    ? '12 PM'
                    : `${parseInt(time) - 12} PM`
                ),
              },
              {
                label: 'Dates',
                value: dates.map((d) => d),
              },
              {
                label: 'Expiration Date',
                value: selectedReport.scheduleEndDate
                  ? new Date(
                      selectedReport.scheduleEndDate
                    ).toLocaleDateString()
                  : '',
                isVisible: !!(
                  selectedReport.scheduleEndDate &&
                  selectedReport.scheduleEndDate !== 0
                ),
              },
            ],
          },
        ]
      : []),
  ];

  return <SharedReviewLayout sections={reportSections} />;
}
